var _class;
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
import { EditorView } from 'prosemirror-view';
import { EditorState } from 'prosemirror-state';
import { action } from '@ember/object';
import { BlocksParser } from '@intercom/composer-core';
import { BlocksDocument } from '@intercom/composer-core';
import KeymapPluginBuilder from '../config/keymap';
import { history } from 'prosemirror-history';
import { handleUploadRetry } from '@intercom/composer-core';
import { listScrollParents } from '../helpers/scroll';
import InputRules from '../config/input-rules';
import { handleFilePaste, transformPastedHTML, transformPastedSlice, transformPastedText } from '../helpers/paste';
import MessengerCardView from './node-views/messenger-card';
import HtmlBlockView from './node-views/html-block';
import IfElseStatementView from './node-views/if-else-statement';
import TwitterFollowButtonView from './node-views/twitter-follow-button';
import FacebookLikeButtonView from './node-views/facebook-like-button';
import { BlocksSerializer } from '@intercom/composer-core';
import { gapCursor } from 'prosemirror-gapcursor';
import SelectedNodeDecorationPlugin from '../config/selected-node-decoration-plugin';
import SelectedSectionNodeDecorationPlugin from '../config/selected-section-node-decoration-plugin';
import { autoLink } from '../config/auto-link';
import { filterContainerDeletion } from '../config/filter-container-deletion';
import PlaceholderPlugin from '@intercom/composer-ember/lib/config/placeholder-plugin';
import UpdateImageSizesPlugin from '../config/update-image-sizes-plugin';
import SocialView from '@intercom/composer-ember/lib/composer/node-views/social';
import SpacerView from '@intercom/composer-ember/lib/composer/node-views/spacer';
import MaxCharacterCountPlugin from '../config/max-character-count';
import { selectAllTextInBlock } from '../helpers/selection';
import { nodeIsInLayoutOrSection } from '../helpers/table';
import { columnResizing, tableEditing, CellSelection, cellAround } from 'prosemirror-tables';
import HorizontalRuleView from './node-views/horizontal-rule';
import { HighlightContentPlugin } from '../config/highlight-content';
import UnsupportedElementView from '@intercom/composer-ember/lib/composer/node-views/unsupported-element';

/**
 * The editor object is responsible for managing the editing surface of the composer.
 *
 * It looks after creating Prosemirror's EditorView object and wiring up its callbacks such that changes
 * in the editor are visible to ember's change tracking system through the `state`
 * property on the composer object.
 */
let Editor = (_class = class Editor {
  constructor({
    elem,
    composer,
    onChange,
    analytics
  }) {
    _defineProperty(this, "editorView", void 0);
    _defineProperty(this, "doc", void 0);
    _defineProperty(this, "composer", void 0);
    _defineProperty(this, "keyboardEventDelegate", void 0);
    _defineProperty(this, "scrollingElements", []);
    _defineProperty(this, "analytics", void 0);
    this.composer = composer;
    this.doc = composer.blocksDoc.excludingAttachmentListBlock;
    this.editorView = this.createView(elem, this.doc, onChange);
    this.analytics = analytics;
  }
  createView(elem, doc, onChange) {
    let prosemirrorDoc = this.parseBlocks(doc);
    this.attachScrollHandlers(elem);
    let optionalPlugins = [];
    if (this.composer.config.enableAutoLink) {
      optionalPlugins.push(autoLink());
    }
    if (this.composer.config.tables?.preventContainerDeletion) {
      optionalPlugins.push(filterContainerDeletion());
    }
    if (this.composer.config.allowedBlocks.includes('placeholder')) {
      optionalPlugins.push(new PlaceholderPlugin());
    }
    if (this.composer.config.resizeImages) {
      optionalPlugins.push(new UpdateImageSizesPlugin(this.composer));
    }
    if (this.composer.config.tables?.resizeColumns) {
      optionalPlugins.push(columnResizing({
        cellMinWidth: 50,
        lastColumnResizable: false
      }));
    }
    if (this.composer.config.enableHighlighting) {
      optionalPlugins.push(new HighlightContentPlugin());
    }
    if (this.composer.config.maxCharacterCount) {
      optionalPlugins.push(new MaxCharacterCountPlugin(this.composer.config.maxCharacterCount));
    }
    return new EditorView(elem, {
      state: EditorState.create({
        doc: prosemirrorDoc,
        plugins: [new KeymapPluginBuilder(this.composer.config, this.composer.schema).plugin, new InputRules(this.composer.config, this.composer.schema).plugin, history(), gapCursor(), tableEditing({
          allowTableNodeSelection: true
        }), new SelectedNodeDecorationPlugin(), new SelectedSectionNodeDecorationPlugin(this.composer), ...optionalPlugins]
      }),
      attributes: {
        class: `embercom-prosemirror-composer-editor`,
        role: 'textbox',
        dir: this.composer.config.textDirection,
        'data-insertable': 'true',
        translate: 'no'
      },
      dispatchTransaction: transaction => {
        let newState = this.editorView.state.apply(transaction);
        this.editorView.updateState(newState);
        if (transaction.docChanged) {
          this.doc = new BlocksSerializer(this.composer.config, this.composer.schema).serialize(newState.doc);
          this.doc.stepsSinceLastChange = transaction.steps;
        }
        onChange(newState, transaction.docChanged);
      },
      handlePaste: (_, e) => {
        return handleFilePaste(e, this.composer);
      },
      transformPastedHTML: html => {
        let result = transformPastedHTML(html, this.composer.config);
        this.analytics.trackAnalyticsEvent('pasted', {
          length: html.length,
          provider: result.provider
        });
        return result.html;
      },
      clipboardTextParser: (text, $context) => {
        return transformPastedText(text, this.composer, $context);
      },
      transformPasted: slice => {
        return transformPastedSlice(slice, this.composer, this.editorView);
      },
      nodeViews: {
        twitterFollowButton: (node, _view, _getPos) => {
          return new TwitterFollowButtonView(node, this.composer.config);
        },
        facebookLikeButton: (node, _view, _getPos) => {
          return new FacebookLikeButtonView(node, this.composer.config);
        },
        ifElseStatement: (node, _view, _getPos) => {
          return new IfElseStatementView(node, this.composer.config);
        },
        messengerCard: (node, _view, _getPos) => {
          return new MessengerCardView(node, this.composer.config);
        },
        html: (node, _view, _getPos) => {
          return new HtmlBlockView(node, this.composer.config);
        },
        social: (node, _view, _getPos) => {
          return new SocialView(node, this.composer.config);
        },
        unsupportedElement: (node, _view, _getPos) => {
          return new UnsupportedElementView(node);
        },
        spacer: (node, _view, _getPos) => {
          return new SpacerView(node, this.composer.config);
        },
        horizontalRule: (node, _view, _getPos) => {
          return new HorizontalRuleView(node, this.composer.config);
        }
      },
      handleTripleClickOn(view, _pos, node, nodePos, _event, direct) {
        let {
          state,
          dispatch
        } = view;
        let resolvedPosition = state.doc.resolve(nodePos);
        // don't want to change this behavior in Articles tables
        if (nodeIsInLayoutOrSection(resolvedPosition, state)) {
          if (direct) {
            selectAllTextInBlock(state, dispatch, node, nodePos);
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        click: (_, e) => {
          handleUploadRetry(this.composer, e);
          return false;
        },
        dblclick: (view, event) => {
          let pos = view.posAtCoords({
            left: event.clientX,
            top: event.clientY
          });
          if (!pos) {
            return false;
          }
          let $pos = view.state.doc.resolve(pos.pos);
          let cell = cellAround($pos);
          if (cell) {
            let currentSelection = view.state.selection;
            let isTextSelected = currentSelection.from !== currentSelection.to;
            if (!isTextSelected) {
              event.preventDefault();
              let cellSelection = CellSelection.create(view.state.doc, cell.pos, cell.pos);
              let tr = this.composer.state.prosemirrorState.tr.setSelection(cellSelection);
              this.composer.commands.dispatch(tr);
              return true;
            }
          }
          return false;
        },
        mouseup: () => {
          if (this.composer?.state) {
            this.composer.state.mouseIsDown = false;
          }
          return false;
        },
        mousedown: () => {
          if (this.composer?.state) {
            this.composer.state.mouseIsDown = true;
          }
          return false;
        },
        mousemove: (_, e) => {
          if (this.composer?.state) {
            this.composer.state.isEditing = false;
          }
          this.composer.updateHoverInfo(e);
          return false;
        },
        scroll: () => {
          this.composer.scrollOrResizeHandler();
          return false;
        },
        keydown: (_, e) => {
          if (this.keyboardEventDelegate) {
            return this, this.keyboardEventDelegate(e);
          }
          return false;
        },
        focus: () => {
          this.composer.onFocusHandler();
          this.composer.state.showSectionType = true;
          return false;
        },
        blur: () => {
          this.composer.onBlurHandler();
          return false;
        }
      }
    });
  }
  reloadBlocks() {
    this.doc = this.composer.blocksDoc.excludingAttachmentListBlock;
    let newState = EditorState.create({
      doc: this.parseBlocks(this.doc),
      plugins: this.editorView.state.plugins
    });
    this.editorView.updateState(newState);
  }
  parseBlocks(doc) {
    try {
      return new BlocksParser(this.composer.schema).parse(doc, this.composer.config);
    } catch (e) {
      // if we're given an invalid document, fallback to an empty document
      console.error('Error parsing document, falling back to empty document', e);
      return new BlocksParser(this.composer.schema).parse(new BlocksDocument(), this.composer.config);
    }
  }
  attachScrollHandlers(element) {
    this.removeScrollHandlers();
    this.scrollingElements = listScrollParents(element);
    this.scrollingElements.push(element);
    this.scrollingElements.forEach(e => {
      e.addEventListener('scroll', this.composer.scrollOrResizeHandler, {
        passive: true
      });
    });
  }
  removeScrollHandlers() {
    this.scrollingElements.forEach(e => {
      e.removeEventListener('scroll', this.composer.scrollOrResizeHandler);
    });
    this.scrollingElements = [];
  }
  destroy() {
    this.removeScrollHandlers();
    this.editorView.destroy();
  }
  registerKeyboardEventDelegate(delegate) {
    this.keyboardEventDelegate = delegate;
  }
  deregisterKeyboardEventDelegate(delegate) {
    if (this.keyboardEventDelegate === delegate) {
      this.keyboardEventDelegate = undefined;
    }
  }
}, _applyDecoratedDescriptor(_class.prototype, "registerKeyboardEventDelegate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerKeyboardEventDelegate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deregisterKeyboardEventDelegate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "deregisterKeyboardEventDelegate"), _class.prototype), _class);
export { Editor as default };